<script setup lang="ts">
import { $getLineItemStatuses } from '@/api/brandings'
import { uniqBy } from 'lodash'

interface IProps {
  modelValue?: any,
  label?: string,
  projectType?: any,
  selectOptions?: any[] | null,
  /**
   * Optional array of itemStatus IDs that should be disabled for selection.
   * If null or undefined, no itemStatuses will be disabled.
   */
  disabledItemStatuses?: number[] | null,
}

const props = withDefaults(defineProps<IProps>(), {
  modelValue: () => [],
  label: 'Status',
  projectType: null,
  selectOptions: null,
  disabledItemStatuses: null
})

interface CustomEvents {
  'update:modelValue': (newValue: any) => void;
}

const emit = defineEmits<CustomEvents>()

interface ItemStatus {
  id: number
  name: string
}

const itemStatuses = ref<ItemStatus[]>([])
const itemStatusesCurrentPage = ref<number>(1)
const itemStatusesPerPage: number = 10
const itemStatusesSearchStr = ref<string>('')
const isNextItemStatusesPageExist = ref<boolean>(false)
const itemStatusesLoading = ref<boolean>(false)

const itemStatusOptions = computed(() => {
  if (props.selectOptions) {
    return props.selectOptions
  } else {
    return itemStatuses.value
  }
})

const getItemStatuses = async () => {
  itemStatusesLoading.value = true
  try {
    const res = await $getLineItemStatuses({
      search: itemStatusesSearchStr.value,
      per_page: itemStatusesPerPage,
      page: itemStatusesCurrentPage.value,
      project_type: props.projectType ? props.projectType : undefined
    })

    // Append the new itemStatuses to the existing list
    // Use Lodash's uniqBy to merge arrays while ensuring uniqueness based on 'id'
    itemStatuses.value = uniqBy([...itemStatuses.value, ...res.data.results], 'id')
    isNextItemStatusesPageExist.value = res.data.next
  } catch (e) {
    console.log(e)
  } finally {
    itemStatusesLoading.value = false
  }
}

const onFocusItemStatusSelect = async () => {
  if (!itemStatuses.value.length && !props.selectOptions) {
    itemStatuses.value = []
    itemStatusesCurrentPage.value = 1
    await getItemStatuses()
  }
}

const onLoadMoreItemStatuses = async () => {
  itemStatusesCurrentPage.value++ // Increment the current page to load the next page of itemStatuses
  await getItemStatuses()
}

const clearAndGetItemStatuses = (searchStr: string) => {
  itemStatuses.value = []
  itemStatusesCurrentPage.value = 1 // Set current page => 1 to load initial first page of itemStatuses
  itemStatusesSearchStr.value = searchStr
  getItemStatuses()
}

const handleItemStatusSearch = useDebounceFn((searchStr: string) => {
  if (!props.selectOptions) {
    clearAndGetItemStatuses(searchStr)
  }
}, 500)

defineExpose({ clearAndGetItemStatuses })
</script>

<template>
  <VAutocomplete
    :model-value="modelValue"
    :items="[...itemStatusOptions]"
    return-object
    item-title="name"
    item-value="id"
    :label="label"
    density="compact"
    :no-filter="!selectOptions"
    multiple
    clearable
    @update:modelValue="newValue => $emit('update:modelValue', newValue)"
    @update:focused="onFocusItemStatusSelect"
    @update:search="handleItemStatusSearch"
  >
    <template
      v-if="disabledItemStatuses"
      #item="{ item, index, props }"
    >
      <VListItem
        v-bind="props"
        :disabled="!!disabledItemStatuses ? disabledItemStatuses.includes(item?.value) : false"
      />
    </template>
    <template #selection="{ item, index }">
      <div class="d-flex align-center">
        <v-chip v-if="index < 1">
          <span>{{ item.title }}</span>
        </v-chip>
        <span
          v-if="index === 1"
          class="text-grey text-caption align-self-center"
        >
          (+{{ modelValue?.length - 1 }} others)
        </span>
      </div>
    </template>
    <template #no-data>
      <div
        v-if="!!itemStatusesLoading"
        class="d-flex justify-center align-center"
      >
        Loading...
        <VProgressCircular
          class="margin-left-5"
          size="22"
          indeterminate
          color="white"
        />
      </div>
      <div
        v-else
        class="d-flex justify-center align-center"
      >
        Statuses not found
      </div>
    </template>
    <template
      v-if="isNextItemStatusesPageExist"
      #append-item
    >
      <VBtn
        color="default"
        block
        :disabled="!!itemStatusesLoading"
        @click="onLoadMoreItemStatuses"
      >
        Load more...
        <VProgressCircular
          v-if="!!itemStatusesLoading"
          class="margin-left-5"
          size="22"
          indeterminate
          color="white"
        />
      </VBtn>
    </template>
  </VAutocomplete>
</template>

<style lang="scss">

</style>
